/**
 * Organization Management Page
 *
 * Handles list, edit, settings, features, and wizard views
 * depending on the current URL:
 *   /superadmin/organizations              -> list
 *   /superadmin/organizations/:id          -> edit general info
 *   /superadmin/organizations/:id/settings -> settings
 *   /superadmin/organizations/:id/features -> feature flags
 *   /superadmin/organizations/:id/wizard   -> wizard step configuration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import Grid from '@mui/material/GridLegacy';
import {
  Box, AppBar, Toolbar, IconButton, Typography, Button, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, CircularProgress,
  Tabs, Tab, Switch, FormControlLabel, Divider, Stack, Card, CardContent,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack, Business, Refresh, Add, Edit, ContentCopy,
  Settings, ToggleOn, Check, Close, Save, Link as LinkIcon, Person,
  AutoAwesome, PersonAdd, AccountBalance, Visibility, VisibilityOff,
  Delete, RestoreFromTrash, PowerSettingsNew, DeleteOutline,
} from '@mui/icons-material';

/** Determine which sub-page we are on based on pathname */
const getSubView = (pathname) => {
  if (pathname.endsWith('/settings')) return 'settings';
  if (pathname.endsWith('/features')) return 'features';
  if (pathname.endsWith('/wizard')) return 'wizard';
  if (pathname.endsWith('/payment')) return 'payment';
  if (/\/organizations\/\d+$/.test(pathname)) return 'edit';
  return 'list';
};

/** Shared dark-theme TextField style */
const darkInputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#818cf8' },
  '& .MuiSelect-icon': { color: '#94a3b8' },
};

const OrganizationManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { api, isAuthenticated, loading: authLoading } = useSuperAdmin();

  const subView = getSubView(location.pathname);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate('/superadmin/login');
  }, [isAuthenticated, authLoading, navigate]);

  /* ── LIST ─────────────────────────────────── */
  const [organizations, setOrganizations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '', code: '', slug: '', email: '',
    organization_type: 'RTA', region: '', max_users: 100,
    // Admin fields
    admin_username: '', admin_password: '', admin_email: '',
    admin_first_name: '', admin_last_name: '',
  });
  const [addAdminForm, setAddAdminForm] = useState({
    username: '', password: '', email: '', first_name: '', last_name: '',
  });
  const [addAdminSaving, setAddAdminSaving] = useState(false);
  const [cloneForm, setCloneForm] = useState({
    target_name: '', target_code: '', target_type: 'PTA',
    clone_settings: true, clone_features: true,
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteConfirmOrg, setDeleteConfirmOrg] = useState(null);
  const [orgActionLoadingId, setOrgActionLoadingId] = useState(null);

  const fetchOrganizations = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await api.get(`/api/superadmin/organizations/${showDeleted ? '?deleted=1' : ''}`);
      setOrganizations(res.data);
    } catch {
      setError('Failed to load organizations');
    } finally {
      setListLoading(false);
    }
  }, [api, showDeleted]);

  useEffect(() => {
    if (subView === 'list') fetchOrganizations();
  }, [subView, fetchOrganizations]);

  const handleActivateOrg = async (org) => {
    setError(''); setSuccess('');
    setOrgActionLoadingId(org.id);
    try {
      await api.post(`/api/superadmin/organizations/${org.id}/activate/`);
      setSuccess(`"${org.name}" activated`);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate organization');
    } finally {
      setOrgActionLoadingId(null);
    }
  };

  const handleDeactivateOrg = async (org) => {
    setError(''); setSuccess('');
    setOrgActionLoadingId(org.id);
    try {
      await api.post(`/api/superadmin/organizations/${org.id}/deactivate/`);
      setSuccess(`"${org.name}" deactivated`);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate organization');
    } finally {
      setOrgActionLoadingId(null);
    }
  };

  const handleDeleteOrg = async () => {
    if (!deleteConfirmOrg) return;
    setError(''); setSuccess('');
    setOrgActionLoadingId(deleteConfirmOrg.id);
    try {
      await api.delete(`/api/superadmin/organizations/${deleteConfirmOrg.id}/`);
      setSuccess(`"${deleteConfirmOrg.name}" deleted`);
      setDeleteConfirmOrg(null);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete organization');
    } finally {
      setOrgActionLoadingId(null);
    }
  };

  const handleRestoreOrg = async (org) => {
    setError(''); setSuccess('');
    setOrgActionLoadingId(org.id);
    try {
      await api.post(`/api/superadmin/organizations/${org.id}/restore/`);
      setSuccess(`"${org.name}" restored`);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore organization');
    } finally {
      setOrgActionLoadingId(null);
    }
  };

  const handleCreate = async () => {
    setError('');
    try {
      // Extract admin data from form
      const { admin_username, admin_password, admin_email, admin_first_name, admin_last_name, ...orgData } = createForm;

      // Log for debugging
      console.log('Create form data:', { ...createForm, admin_password: '***' });
      console.log('Org data after destructure:', orgData);

      // Client-side validation for required fields
      if (!orgData.name || !orgData.name.trim()) {
        setError('Organization name is required');
        return;
      }
      if (!orgData.code || !orgData.code.trim()) {
        setError('Organization code is required');
        return;
      }
      if (!orgData.email || !orgData.email.trim()) {
        console.error('Email field empty! CreateForm keys:', Object.keys(createForm));
        console.error('Email value type:', typeof createForm.email, 'value:', `"${createForm.email}"`);
        setError('Organization email is required');
        return;
      }
      if (!orgData.slug || !orgData.slug.trim()) {
        setError('Organization slug is required');
        return;
      }

      // Step 1: Create organization
      const res = await api.post('/api/superadmin/organizations/', orgData);
      const newOrgId = res.data.id;

      // Step 2: Create admin if credentials provided
      if (admin_username && admin_password && admin_email) {
        try {
          await api.post(`/api/superadmin/organizations/${newOrgId}/add-admin/`, {
            username: admin_username,
            password: admin_password,
            email: admin_email,
            first_name: admin_first_name,
            last_name: admin_last_name,
          });
          setSuccess(`Organization "${res.data.name}" created with admin "${admin_username}"!`);
        } catch (adminErr) {
          setSuccess(`Organization "${res.data.name}" created, but admin creation failed: ${adminErr.response?.data?.error || 'Unknown error'}`);
        }
      } else {
        setSuccess(`Organization "${res.data.name}" created successfully! (No admin created - add one later)`);
      }

      setCreateOpen(false);
      setCreateForm({
        name: '', code: '', slug: '', email: '', organization_type: 'RTA', region: '', max_users: 100,
        admin_username: '', admin_password: '', admin_email: '', admin_first_name: '', admin_last_name: '',
      });
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create organization');
    }
  };

  const handleClone = async () => {
    setError('');
    try {
      const res = await api.post(`/api/superadmin/organizations/${cloneSource.id}/clone/`, cloneForm);
      setSuccess(res.data.message);
      setCloneOpen(false);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clone organization');
    }
  };

  const openClone = (org) => {
    setCloneSource(org);
    setCloneForm({ ...cloneForm, target_name: `${org.name} - Clone`, target_code: `${org.code}2` });
    setCloneOpen(true);
  };

  const handleAddAdmin = async () => {
    setError('');
    setAddAdminSaving(true);
    try {
      await api.post(`/api/superadmin/organizations/${org.id}/add-admin/`, addAdminForm);
      setSuccess(`Admin "${addAdminForm.username}" added successfully!`);
      setAddAdminOpen(false);
      setAddAdminForm({ username: '', password: '', email: '', first_name: '', last_name: '' });
      fetchOrg(); // Refresh org data
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add admin');
    }
    setAddAdminSaving(false);
  };

  const openAddAdmin = () => {
    setAddAdminForm({ username: '', password: '', email: '', first_name: '', last_name: '' });
    setAddAdminOpen(true);
  };

  /* ── EDIT ─────────────────────────────────── */
  const [org, setOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const fetchOrg = useCallback(async () => {
    if (!id) return;
    setOrgLoading(true);
    try {
      const res = await api.get(`/api/superadmin/organizations/${id}/`);
      setOrg(res.data);
      setEditForm({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        address: res.data.address || '',
        website: res.data.website || '',
        region: res.data.region || '',
        city: res.data.city || '',
        status: res.data.status || 'active',
        max_users: res.data.max_users || 100,
        primary_color: res.data.primary_color || '#1976d2',
        secondary_color: res.data.secondary_color || '#dc004e',
      });
    } catch {
      setError('Failed to load organization');
    } finally {
      setOrgLoading(false);
    }
  }, [api, id]);

  const handleEditSave = async () => {
    setEditSaving(true);
    setError('');
    try {
      await api.put(`/api/superadmin/organizations/${id}/`, editForm);
      setSuccess('Organization updated successfully');
      fetchOrg();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update organization');
    } finally {
      setEditSaving(false);
    }
  };

  /* ── SETTINGS ─────────────────────────────── */
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!id) return;
    setSettingsLoading(true);
    try {
      const res = await api.get(`/api/superadmin/organizations/${id}/settings/`);
      setSettings(res.data);
      setOrg({ name: res.data.organization_name, id: res.data.organization_id });
    } catch {
      setError('Failed to load settings');
    } finally {
      setSettingsLoading(false);
    }
  }, [api, id]);

  const handleSettingsSave = async () => {
    setSettingsSaving(true);
    setError('');
    try {
      await api.put(`/api/superadmin/organizations/${id}/settings/`, settings);
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  /* ── FEATURES ─────────────────────────────── */
  const [features, setFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);

  const fetchFeatures = useCallback(async () => {
    if (!id) return;
    setFeaturesLoading(true);
    try {
      const res = await api.get(`/api/superadmin/organizations/${id}/features/`);
      setFeatures(res.data.features || []);
      setOrg({ name: res.data.organization_name, id: res.data.organization_id });
    } catch {
      setError('Failed to load features');
    } finally {
      setFeaturesLoading(false);
    }
  }, [api, id]);

  const handleFeaturesSave = async () => {
    setFeaturesSaving(true);
    setError('');
    try {
      const featureMap = {};
      features.forEach(f => { featureMap[f.name] = f.enabled; });
      await api.put(`/api/superadmin/organizations/${id}/features/`, { features: featureMap });
      setSuccess('Features updated successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update features');
    } finally {
      setFeaturesSaving(false);
    }
  };

  const toggleFeature = (name) => {
    setFeatures(prev => prev.map(f => f.name === name ? { ...f, enabled: !f.enabled } : f));
  };

  /* ── WIZARD CONFIG ─────────────────────────── */
  const [wizardConfig, setWizardConfig] = useState(null);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardSaving, setWizardSaving] = useState(false);

  const fetchWizardConfig = useCallback(async () => {
    if (!id) return;
    setWizardLoading(true);
    try {
      const res = await api.get(`/api/superadmin/organizations/${id}/wizard-config/`);
      setWizardConfig(res.data.wizard_steps || {});
      setOrg({ name: res.data.organization_name, id: res.data.organization_id });
    } catch {
      setError('Failed to load wizard configuration');
    } finally {
      setWizardLoading(false);
    }
  }, [api, id]);

  const handleWizardSave = async () => {
    setWizardSaving(true);
    setError('');
    try {
      await api.put(`/api/superadmin/organizations/${id}/wizard-config/`, { wizard_steps: wizardConfig });
      setSuccess('Wizard configuration saved successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save wizard configuration');
    } finally {
      setWizardSaving(false);
    }
  };

  const toggleWizardStep = (stepKey) => {
    setWizardConfig(prev => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey],
        enabled: !prev[stepKey]?.enabled
      }
    }));
  };

  const updateWizardStepLabel = (stepKey, label) => {
    setWizardConfig(prev => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey],
        label
      }
    }));
  };

  /* ── PAYMENT GATEWAY ────────────────────────── */
  const [paymentConfig, setPaymentConfig] = useState({
    jazzcash_enabled: false,
    jazzcash_sandbox_mode: true,
    jazzcash_merchant_id: '',
    jazzcash_password: '',
    jazzcash_hash_key: '',
    jazzcash_return_url: '',
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({ jazzcash_password: false, jazzcash_hash_key: false });

  const fetchPaymentConfig = useCallback(async () => {
    if (!id) return;
    setPaymentLoading(true);
    try {
      const res = await api.get(`/api/superadmin/organizations/${id}/settings/`);
      setPaymentConfig({
        jazzcash_enabled: res.data.jazzcash_enabled ?? false,
        jazzcash_sandbox_mode: res.data.jazzcash_sandbox_mode ?? true,
        jazzcash_merchant_id: res.data.jazzcash_merchant_id || '',
        jazzcash_password: res.data.jazzcash_password || '',
        jazzcash_hash_key: res.data.jazzcash_hash_key || '',
        jazzcash_return_url: res.data.jazzcash_return_url || '',
      });
      setOrg({ name: res.data.organization_name, id: res.data.organization_id });
    } catch {
      setError('Failed to load payment gateway configuration');
    } finally {
      setPaymentLoading(false);
    }
  }, [api, id]);

  const handlePaymentSave = async () => {
    setPaymentSaving(true);
    setError('');
    try {
      await api.put(`/api/superadmin/organizations/${id}/settings/`, paymentConfig);
      setSuccess('Payment gateway configuration saved successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save payment gateway configuration');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handlePaymentChange = (field, value) => {
    setPaymentConfig(prev => ({ ...prev, [field]: value }));
  };

  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Load data when sub-view or id changes
  useEffect(() => {
    setError('');
    setSuccess('');
    if (subView === 'edit') fetchOrg();
    if (subView === 'settings') fetchSettings();
    if (subView === 'features') fetchFeatures();
    if (subView === 'wizard') fetchWizardConfig();
    if (subView === 'payment') fetchPaymentConfig();
  }, [subView, id, fetchOrg, fetchSettings, fetchFeatures, fetchWizardConfig, fetchPaymentConfig]);

  const orgName = org?.name || `Organization #${id}`;
  const tabValue = subView === 'edit' ? 0 : subView === 'settings' ? 1 : subView === 'features' ? 2 : subView === 'wizard' ? 3 : subView === 'payment' ? 4 : false;
  const statusColor = { active: 'success', inactive: 'default', suspended: 'error', trial: 'warning' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', bgcolor: '#0f172a' }}>

      {/* AppBar */}
      <AppBar position="sticky" elevation={0} sx={{ background: 'transparent', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Toolbar sx={{ px: { xs: 2, sm: 4 } }}>
          <IconButton color="inherit" onClick={() => subView === 'list' ? navigate('/superadmin/dashboard') : navigate('/superadmin/organizations')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Business sx={{ mr: 1.5, color: '#818cf8' }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: 'white', fontSize: { xs: '0.95rem', sm: '1.2rem' } }}>
            {subView === 'list' ? 'Organization Management' : orgName}
          </Typography>

          {subView === 'list' && (
            <>
              <IconButton color="inherit" onClick={fetchOrganizations} sx={{ mr: 1 }}><Refresh /></IconButton>
              <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
                sx={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                New Org
              </Button>
            </>
          )}

          {subView !== 'list' && (
            <Tabs value={tabValue} textColor="inherit"
              TabIndicatorProps={{ style: { background: '#818cf8' } }}
              sx={{ '& .MuiTab-root': { color: '#94a3b8', minWidth: 90 }, '& .Mui-selected': { color: 'white' } }}>
              <Tab label="General" onClick={() => navigate(`/superadmin/organizations/${id}`)} />
              <Tab label="Settings" onClick={() => navigate(`/superadmin/organizations/${id}/settings`)} />
              <Tab label="Features" onClick={() => navigate(`/superadmin/organizations/${id}/features`)} />
              <Tab label="Wizard" onClick={() => navigate(`/superadmin/organizations/${id}/wizard`)} />
              <Tab label="Payment Gateway" onClick={() => navigate(`/superadmin/organizations/${id}/payment`)} />
            </Tabs>
          )}
        </Toolbar>
      </AppBar>

      {/* Body */}
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1400, mx: 'auto', width: '100%' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

        {/* ── LIST VIEW ── */}
        {subView === 'list' && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                {showDeleted ? `Deleted Organizations (${organizations.length})` : `Organizations (${organizations.length})`}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={showDeleted ? <ArrowBack fontSize="small" /> : <DeleteOutline fontSize="small" />}
                onClick={() => setShowDeleted(v => !v)}
                sx={{
                  color: showDeleted ? '#f59e0b' : '#94a3b8',
                  borderColor: showDeleted ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.2)',
                }}
              >
                {showDeleted ? 'Back to Active' : 'View Trash'}
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3 }}>
              {listLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#818cf8' }} /></Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                      {['Organization', 'Code', 'Type', 'Admin', 'Status', 'Users', 'License', 'Actions'].map(h => (
                        <TableCell key={h} sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {organizations.map((o) => {
                      const orgUrl = `${window.location.origin}/${(o.code || '').toLowerCase()}/`;
                      return (
                        <TableRow key={o.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Typography sx={{ color: 'white', fontWeight: 500 }}>{o.name}</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{o.email}</Typography>
                            <Box
                              component="a"
                              href={orgUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: '#818cf8',
                                fontSize: '0.75rem',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline', color: '#a5b4fc' },
                              }}
                            >
                              <LinkIcon sx={{ fontSize: '0.875rem' }} />
                              {orgUrl}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Chip label={o.code} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8' }} />
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: 'none' }}>{o.organization_type}</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            {o.has_admin && o.admin ? (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Person sx={{ fontSize: '1rem', color: '#10b981' }} />
                                  <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.875rem' }}>
                                    {o.admin.username}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {o.admin.email}
                                </Typography>
                              </Box>
                            ) : (
                              <Chip label="No Admin" size="small" color="warning" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            <Chip label={o.status} size="small" color={statusColor[o.status] || 'default'} />
                            {showDeleted && o.deleted_at && (
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                Deleted {new Date(o.deleted_at).toLocaleDateString()}
                                {o.deleted_by ? ` by ${o.deleted_by}` : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: 'none' }}>{o.user_count} / {o.max_users}</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>
                            {o.is_licensed
                              ? <Chip icon={<Check />} label="Valid" size="small" color="success" variant="outlined" />
                              : <Chip icon={<Close />} label="Expired" size="small" color="error" variant="outlined" />}
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none', whiteSpace: 'nowrap' }}>
                            {showDeleted ? (
                              <Tooltip title="Restore">
                                <span>
                                  <IconButton size="small" disabled={orgActionLoadingId === o.id}
                                    onClick={() => handleRestoreOrg(o)} sx={{ color: '#10b981' }}>
                                    <RestoreFromTrash fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => navigate(`/superadmin/organizations/${o.id}`)} sx={{ color: '#818cf8' }}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Settings">
                                  <IconButton size="small" onClick={() => navigate(`/superadmin/organizations/${o.id}/settings`)} sx={{ color: '#10b981' }}>
                                    <Settings fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Features">
                                  <IconButton size="small" onClick={() => navigate(`/superadmin/organizations/${o.id}/features`)} sx={{ color: '#f59e0b' }}>
                                    <ToggleOn fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Clone">
                                  <IconButton size="small" onClick={() => openClone(o)} sx={{ color: '#ec4899' }}>
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={o.status === 'active' ? 'Deactivate' : 'Activate'}>
                                  <span>
                                    <IconButton size="small" disabled={orgActionLoadingId === o.id}
                                      onClick={() => o.status === 'active' ? handleDeactivateOrg(o) : handleActivateOrg(o)}
                                      sx={{ color: o.status === 'active' ? '#f59e0b' : '#10b981' }}>
                                      <PowerSettingsNew fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => setDeleteConfirmOrg(o)} sx={{ color: '#ef4444' }}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {organizations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#64748b', borderBottom: 'none' }}>
                          {showDeleted ? 'No deleted organizations.' : 'No organizations found. Create your first organization.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </>
        )}

        {/* ── EDIT VIEW ── */}
        {subView === 'edit' && (
          orgLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#818cf8' }} /></Box>
          ) : (
            <Paper sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>General Information</Typography>
                <Button variant="contained"
                  startIcon={editSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  onClick={handleEditSave} disabled={editSaving}
                  sx={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                  {editSaving ? 'Saving\u2026' : 'Save Changes'}
                </Button>
              </Box>
              <Grid container spacing={3}>
                {[
                  { key: 'name', label: 'Organization Name', xs: 12, sm: 6 },
                  { key: 'email', label: 'Email', xs: 12, sm: 6 },
                  { key: 'phone', label: 'Phone', xs: 12, sm: 6 },
                  { key: 'website', label: 'Website', xs: 12, sm: 6 },
                  { key: 'region', label: 'Region', xs: 12, sm: 4 },
                  { key: 'city', label: 'City', xs: 12, sm: 4 },
                  { key: 'max_users', label: 'Max Users', xs: 12, sm: 4, type: 'number' },
                  { key: 'address', label: 'Address', xs: 12 },
                ].map(f => (
                  <Grid item xs={f.xs} sm={f.sm} key={f.key}>
                    <TextField fullWidth label={f.label} type={f.type || 'text'}
                      value={editForm[f.key] !== undefined ? editForm[f.key] : ''}
                      onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                      sx={darkInputSx} />
                  </Grid>
                ))}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="Status" value={editForm.status || 'active'}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} sx={darkInputSx}>
                    {['active', 'inactive', 'suspended', 'trial'].map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Primary Color" type="color"
                    value={editForm.primary_color || '#1976d2'}
                    onChange={(e) => setEditForm({ ...editForm, primary_color: e.target.value })}
                    sx={darkInputSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Secondary Color" type="color"
                    value={editForm.secondary_color || '#dc004e'}
                    onChange={(e) => setEditForm({ ...editForm, secondary_color: e.target.value })}
                    sx={darkInputSx} />
                </Grid>
              </Grid>
              {org && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2 }}>Read-only info</Typography>
                  <Grid container spacing={2}>
                    {/* Portal URL - full width */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(99,102,241,0.1)', borderRadius: 2, border: '1px solid rgba(99,102,241,0.3)' }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>Portal URL</Typography>
                        <Box
                          component="a"
                          href={`${window.location.origin}/${(org.code || '').toLowerCase()}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#818cf8',
                            fontWeight: 500,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline', color: '#a5b4fc' },
                          }}
                        >
                          <LinkIcon sx={{ fontSize: '1rem' }} />
                          {`${window.location.origin}/${(org.code || '').toLowerCase()}/`}
                        </Box>
                      </Box>
                    </Grid>
                    {[
                      { label: 'Code', value: org.code },
                      { label: 'Type', value: org.organization_type },
                      { label: 'Created', value: org.created_at ? new Date(org.created_at).toLocaleDateString() : '-' },
                      { label: 'License', value: org.is_licensed ? 'Valid' : 'Expired' },
                    ].map(item => (
                      <Grid item xs={6} sm={3} key={item.label}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>{item.label}</Typography>
                          <Typography sx={{ color: 'white', fontWeight: 500 }}>{item.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                    {/* Admin Info */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: org.has_admin ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', borderRadius: 2, border: `1px solid ${org.has_admin ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Person sx={{ fontSize: '1.25rem', color: org.has_admin ? '#10b981' : '#f59e0b' }} />
                          <Typography variant="caption" sx={{ color: '#64748b' }}>Organization Admin</Typography>
                        </Box>
                        {org.has_admin && org.admin ? (
                          <Box>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>
                              {org.admin.first_name || org.admin.last_name ? `${org.admin.first_name || ''} ${org.admin.last_name || ''}`.trim() : org.admin.username}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8' }}>Username: {org.admin.username}</Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8' }}>Email: {org.admin.email}</Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography sx={{ color: '#f59e0b', fontWeight: 500, mb: 1 }}>No admin assigned yet</Typography>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PersonAdd />}
                              onClick={openAddAdmin}
                              sx={{ background: 'linear-gradient(135deg,#10b981,#059669)', '&:hover': { background: 'linear-gradient(135deg,#059669,#047857)' } }}
                            >
                              Add Admin
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          )
        )}

        {/* ── SETTINGS VIEW ── */}
        {subView === 'settings' && (
          settingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#818cf8' }} /></Box>
          ) : settings ? (
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained"
                  startIcon={settingsSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  onClick={handleSettingsSave} disabled={settingsSaving}
                  sx={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                  {settingsSaving ? 'Saving\u2026' : 'Save Settings'}
                </Button>
              </Box>

              {[
                {
                  title: 'Branding', color: '#6366f1', fields: [
                    { key: 'app_name', label: 'App Name' }, { key: 'app_short_name', label: 'Short Name' },
                    { key: 'tagline', label: 'Tagline', sm: 12 }, { key: 'permit_prefix', label: 'Permit Prefix' },
                    { key: 'chalan_prefix', label: 'Chalan Prefix' }, { key: 'document_watermark', label: 'Document Watermark' },
                  ]
                },
                {
                  title: 'Authority Info', color: '#10b981', fields: [
                    { key: 'authority_name', label: 'Authority Name', sm: 12 }, { key: 'authority_code', label: 'Authority Code' },
                    { key: 'authority_phone', label: 'Phone' }, { key: 'authority_website', label: 'Website' },
                    { key: 'authority_address', label: 'Address', sm: 12 },
                  ]
                },
                {
                  title: 'Defaults & Fees', color: '#f59e0b', fields: [
                    { key: 'default_permit_duration_days', label: 'Permit Duration (days)', type: 'number' },
                    { key: 'permit_renewal_notice_days', label: 'Renewal Notice (days)', type: 'number' },
                    { key: 'max_permits_per_owner', label: 'Max Permits per Owner', type: 'number' },
                    { key: 'base_permit_fee', label: 'Base Permit Fee', type: 'number' },
                    { key: 'late_renewal_fee_percent', label: 'Late Renewal Fee %', type: 'number' },
                    { key: 'currency_symbol', label: 'Currency Symbol' },
                  ]
                },
                {
                  title: 'Locale', color: '#8b5cf6', fields: [
                    { key: 'default_language', label: 'Language' }, { key: 'date_format', label: 'Date Format' },
                  ]
                },
                {
                  title: 'Email', color: '#ec4899', fields: [
                    { key: 'email_from_name', label: 'From Name' }, { key: 'email_from_address', label: 'From Email' },
                  ]
                },
              ].map((section) => (
                <Paper key={section.title} sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', bgcolor: `${section.color}15` }}>
                    <Typography sx={{ color: section.color, fontWeight: 600 }}>{section.title}</Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      {section.fields.map(f => (
                        <Grid item xs={12} sm={f.sm || 6} key={f.key}>
                          <TextField fullWidth label={f.label} type={f.type || 'text'}
                            value={settings[f.key] !== undefined ? settings[f.key] : ''}
                            onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                            sx={darkInputSx} />
                        </Grid>
                      ))}
                    </Grid>
                    {section.title === 'Defaults & Fees' && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {[
                          { key: 'require_document_verification', label: 'Require Document Verification' },
                          { key: 'allow_online_payment', label: 'Allow Online Payment' },
                        ].map(f => (
                          <Grid item xs={12} sm={6} key={f.key}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <FormControlLabel
                                control={<Switch checked={!!settings[f.key]}
                                  onChange={(e) => setSettings({ ...settings, [f.key]: e.target.checked })}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: section.color },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: section.color }
                                  }} />}
                                label={<Typography sx={{ color: 'white' }}>{f.label}</Typography>}
                                labelPlacement="start" sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                </Paper>
              ))}

              <Paper sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(59,130,246,0.1)' }}>
                  <Typography sx={{ color: '#3b82f6', fontWeight: 600 }}>Notifications & Security</Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    {[
                      { key: 'enable_email_notifications', label: 'Email Notifications' },
                      { key: 'enable_sms_notifications', label: 'SMS Notifications' },
                      { key: 'show_dashboard_stats', label: 'Show Dashboard Stats' },
                      { key: 'show_recent_activity', label: 'Show Recent Activity' },
                      { key: 'enable_audit_logging', label: 'Enable Audit Logging' },
                      { key: 'require_strong_passwords', label: 'Require Strong Passwords' },
                    ].map(f => (
                      <Grid item xs={12} sm={4} key={f.key}>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <FormControlLabel
                            control={<Switch checked={!!settings[f.key]}
                              onChange={(e) => setSettings({ ...settings, [f.key]: e.target.checked })}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3b82f6' }
                              }} />}
                            label={<Typography sx={{ color: 'white', fontSize: '0.9rem' }}>{f.label}</Typography>}
                            labelPlacement="start" sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                          />
                        </Box>
                      </Grid>
                    ))}
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth type="number" label="Session Timeout (min)"
                        value={settings.session_timeout_minutes !== undefined ? settings.session_timeout_minutes : 480}
                        onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) || 480 })}
                        sx={darkInputSx} />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Stack>
          ) : (
            <Alert severity="warning">No settings found for this organization.</Alert>
          )
        )}

        {/* ── FEATURES VIEW ── */}
        {subView === 'features' && (
          featuresLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#818cf8' }} /></Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Feature Toggles ({features.filter(f => f.enabled).length} / {features.length} enabled)
                </Typography>
                <Button variant="contained"
                  startIcon={featuresSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  onClick={handleFeaturesSave} disabled={featuresSaving}
                  sx={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                  {featuresSaving ? 'Saving\u2026' : 'Save Features'}
                </Button>
              </Box>
              {features.length === 0 ? (
                <Alert severity="info">No features configured for this organization.</Alert>
              ) : (
                <Grid container spacing={2}>
                  {features.map((feature) => (
                    <Grid item xs={12} sm={6} md={4} key={feature.name}>
                      <Card sx={{
                        bgcolor: feature.enabled ? 'rgba(99,102,241,0.15)' : '#1e293b',
                        border: `1px solid ${feature.enabled ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)'}`,
                        borderRadius: 2, transition: 'all 0.2s ease',
                      }}>
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                                {feature.display_name || feature.name}
                              </Typography>
                              {feature.description && (
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                  {feature.description}
                                </Typography>
                              )}
                              <Chip label={feature.enabled ? 'Enabled' : 'Disabled'} size="small" sx={{
                                mt: 1,
                                bgcolor: feature.enabled ? 'rgba(16,185,129,0.2)' : 'rgba(107,114,128,0.2)',
                                color: feature.enabled ? '#10b981' : '#6b7280',
                              }} />
                            </Box>
                            <Switch checked={feature.enabled} onChange={() => toggleFeature(feature.name)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' }
                              }} />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )
        )}

        {/* ── WIZARD CONFIG VIEW ── */}
        {subView === 'wizard' && (
          wizardLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#818cf8' }} /></Box>
          ) : wizardConfig ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                    Permit Wizard Configuration
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                    Enable or disable wizard steps for permit creation. Disabled steps will be skipped.
                  </Typography>
                </Box>
                <Button variant="contained"
                  startIcon={wizardSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  onClick={handleWizardSave} disabled={wizardSaving}
                  sx={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                  {wizardSaving ? 'Saving\u2026' : 'Save Configuration'}
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 3, bgcolor: 'rgba(59,130,246,0.15)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}>
                <strong>Note:</strong> Steps marked as "Required" cannot be disabled. Optional steps can be hidden from the permit wizard.
              </Alert>

              <Grid container spacing={2}>
                {Object.entries(wizardConfig)
                  .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
                  .map(([stepKey, stepConfig]) => {
                    const isRequired = stepConfig.required === true;
                    const canDisable = !isRequired;
                    return (
                      <Grid item xs={12} sm={6} key={stepKey}>
                        <Card sx={{
                          bgcolor: stepConfig.enabled ? 'rgba(99,102,241,0.15)' : '#1e293b',
                          border: `1px solid ${stepConfig.enabled ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          opacity: !canDisable ? 0.85 : 1,
                          height: '100%',
                        }}>
                          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            {/* Header with Step Number and Toggle */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <AutoAwesome sx={{ fontSize: '1.2rem', color: stepConfig.enabled ? '#818cf8' : '#64748b' }} />
                                  <Typography sx={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600 }}>
                                    Step {stepConfig.order || 0}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                  <Chip
                                    label={stepConfig.enabled ? 'Enabled' : 'Disabled'}
                                    size="small"
                                    sx={{
                                      bgcolor: stepConfig.enabled ? 'rgba(16,185,129,0.2)' : 'rgba(107,114,128,0.2)',
                                      color: stepConfig.enabled ? '#10b981' : '#6b7280',
                                    }}
                                  />
                                  {isRequired ? (
                                    <Chip
                                      label="Required"
                                      size="small"
                                      sx={{ bgcolor: 'rgba(239,68,68,0.2)', color: '#ef4444' }}
                                    />
                                  ) : (
                                    <Chip
                                      label="Optional"
                                      size="small"
                                      sx={{ bgcolor: 'rgba(59,130,246,0.2)', color: '#3b82f6' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Tooltip title={canDisable ? 'Toggle step visibility' : 'Required step cannot be disabled'}>
                                <span>
                                  <Switch
                                    checked={stepConfig.enabled}
                                    onChange={() => canDisable && toggleWizardStep(stepKey)}
                                    disabled={!canDisable}
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' },
                                      '& .MuiSwitch-switchBase.Mui-disabled': { color: '#64748b' },
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </Box>

                            {/* Step Label Input */}
                            <TextField
                              fullWidth
                              label="Step Label"
                              value={stepConfig.label || ''}
                              onChange={(e) => updateWizardStepLabel(stepKey, e.target.value)}
                              size="small"
                              sx={{
                                mb: 2,
                                ...darkInputSx,
                                '& .MuiOutlinedInput-root': {
                                  ...darkInputSx['& .MuiOutlinedInput-root'],
                                  bgcolor: 'rgba(255,255,255,0.03)',
                                }
                              }}
                            />

                            {/* Description */}
                            {stepConfig.description && (
                              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, fontSize: '0.85rem' }}>
                                {stepConfig.description}
                              </Typography>
                            )}

                            {/* Fields List */}
                            {stepConfig.fields && stepConfig.fields.length > 0 && (
                              <Box sx={{
                                p: 1.5,
                                bgcolor: 'rgba(255,255,255,0.03)',
                                borderRadius: 1,
                                border: '1px solid rgba(255,255,255,0.08)'
                              }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
                                  Fields in this step:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {stepConfig.fields.map((field, idx) => (
                                    <Chip
                                      key={idx}
                                      label={field}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.7rem',
                                        height: 22,
                                        borderColor: 'rgba(255,255,255,0.15)',
                                        color: '#94a3b8',
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            </>
          ) : (
            <Alert severity="warning">No wizard configuration found for this organization.</Alert>
          )
        )}

        {/* \u2500\u2500 PAYMENT GATEWAY VIEW \u2500\u2500 */}
        {subView === 'payment' && (
          paymentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#10b981' }} /></Box>
          ) : (
            <Paper sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AccountBalance sx={{ color: '#10b981', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>Payment Gateway — JazzCash</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Per-organisation JazzCash credentials
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Enable toggle */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={paymentConfig.jazzcash_enabled}
                            onChange={(e) => handlePaymentChange('jazzcash_enabled', e.target.checked)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10b981' } }}
                          />
                        }
                        label={<Box><Typography sx={{ color: 'white', fontWeight: 500 }}>Enable JazzCash</Typography><Typography variant="caption" sx={{ color: '#64748b' }}>Allow online payments via JazzCash</Typography></Box>}
                        sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                        labelPlacement="start"
                      />
                    </Box>
                  </Grid>

                  {/* Sandbox toggle */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={paymentConfig.jazzcash_sandbox_mode}
                            onChange={(e) => handlePaymentChange('jazzcash_sandbox_mode', e.target.checked)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f59e0b' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#f59e0b' } }}
                          />
                        }
                        label={<Box><Typography sx={{ color: 'white', fontWeight: 500 }}>Sandbox Mode</Typography><Typography variant="caption" sx={{ color: '#64748b' }}>Use JazzCash test environment</Typography></Box>}
                        sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                        labelPlacement="start"
                      />
                    </Box>
                  </Grid>

                  {/* Merchant ID */}
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Merchant ID" value={paymentConfig.jazzcash_merchant_id}
                      onChange={(e) => handlePaymentChange('jazzcash_merchant_id', e.target.value)}
                      inputProps={{ autoComplete: 'off' }} sx={darkInputSx} />
                  </Grid>

                  {/* Return URL */}
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Return / Callback URL" value={paymentConfig.jazzcash_return_url}
                      onChange={(e) => handlePaymentChange('jazzcash_return_url', e.target.value)}
                      placeholder="https://yourdomain.com/payment/return"
                      sx={darkInputSx} />
                  </Grid>

                  {/* Password */}
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Merchant Password"
                      type={showSecrets.jazzcash_password ? 'text' : 'password'}
                      value={paymentConfig.jazzcash_password}
                      onChange={(e) => handlePaymentChange('jazzcash_password', e.target.value)}
                      inputProps={{ autoComplete: 'new-password' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => toggleSecretVisibility('jazzcash_password')} edge="end" sx={{ color: '#64748b' }}>
                              {showSecrets.jazzcash_password ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={darkInputSx} />
                  </Grid>

                  {/* Hash Key */}
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Hash Key (Integrity Salt)"
                      type={showSecrets.jazzcash_hash_key ? 'text' : 'password'}
                      value={paymentConfig.jazzcash_hash_key}
                      onChange={(e) => handlePaymentChange('jazzcash_hash_key', e.target.value)}
                      inputProps={{ autoComplete: 'new-password' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => toggleSecretVisibility('jazzcash_hash_key')} edge="end" sx={{ color: '#64748b' }}>
                              {showSecrets.jazzcash_hash_key ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={darkInputSx} />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={paymentSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                    onClick={handlePaymentSave}
                    disabled={paymentSaving}
                    sx={{ background: 'linear-gradient(135deg,#10b981,#059669)', '&:hover': { background: 'linear-gradient(135deg,#059669,#047857)' }, '&:disabled': { opacity: 0.6 } }}
                  >
                    {paymentSaving ? 'Saving...' : 'Save Payment Gateway'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )
        )}
      </Box>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.15)' } }}>
        <DialogTitle sx={{ color: 'white' }}>Create New Organization</DialogTitle>
        <DialogContent>
          {/* Organization Info Section */}
          <Typography variant="subtitle2" sx={{ color: '#818cf8', fontWeight: 600, mb: 2, mt: 1 }}>
            Organization Details
          </Typography>
          <Grid container spacing={2}>
            {[
              { key: 'name', label: 'Organization Name', xs: 12, sm: 6 },
              { key: 'code', label: 'Code (e.g. PRTA)', xs: 6, sm: 3 },
              { key: 'slug', label: 'Slug (url-friendly)', xs: 6, sm: 3 },
              { key: 'email', label: 'Organization Email', xs: 12, sm: 6 },
              { key: 'region', label: 'Region', xs: 6, sm: 3 },
              { key: 'max_users', label: 'Max Users', xs: 6, sm: 3, type: 'number' },
            ].map(f => (
              <Grid item xs={f.xs} sm={f.sm} key={f.key}>
                <TextField fullWidth label={f.label} type={f.type || 'text'}
                  value={createForm[f.key]}
                  onChange={(e) => setCreateForm({ ...createForm, [f.key]: e.target.value })}
                  sx={darkInputSx} />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Organization Type" value={createForm.organization_type}
                onChange={(e) => setCreateForm({ ...createForm, organization_type: e.target.value })} sx={darkInputSx}>
                {[['RTA', 'Regional Transport Authority'], ['PTA', 'Provincial Transport Authority'],
                ['NTA', 'National Transport Authority'], ['MTA', 'Municipal Transport Authority'], ['OTHER', 'Other']
                ].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          {/* Admin Section */}
          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="subtitle2" sx={{ color: '#10b981', fontWeight: 600, mb: 1 }}>
            Organization Admin (Optional)
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
            Create an admin user who will manage this organization. Leave blank to add later.
          </Typography>
          <Grid container spacing={2}>
            {[
              { key: 'admin_username', label: 'Admin Username', xs: 12, sm: 4 },
              { key: 'admin_email', label: 'Admin Email', xs: 12, sm: 4 },
              { key: 'admin_password', label: 'Password', xs: 12, sm: 4, type: 'password' },
              { key: 'admin_first_name', label: 'First Name', xs: 6 },
              { key: 'admin_last_name', label: 'Last Name', xs: 6 },
            ].map(f => (
              <Grid item xs={f.xs} sm={f.sm} key={f.key}>
                <TextField fullWidth label={f.label} type={f.type || 'text'}
                  value={createForm[f.key]}
                  onChange={(e) => setCreateForm({ ...createForm, [f.key]: e.target.value })}
                  sx={darkInputSx} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" sx={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
            Create Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={cloneOpen} onClose={() => setCloneOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.15)' } }}>
        <DialogTitle sx={{ color: 'white' }}>Clone Organization</DialogTitle>
        <DialogContent>
          {cloneSource && (
            <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(59,130,246,0.15)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}>
              Cloning from: <strong>{cloneSource.name}</strong> ({cloneSource.code})
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="New Organization Name" value={cloneForm.target_name}
                onChange={(e) => setCloneForm({ ...cloneForm, target_name: e.target.value })} sx={darkInputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="New Code" value={cloneForm.target_code}
                onChange={(e) => setCloneForm({ ...cloneForm, target_code: e.target.value.toUpperCase() })} sx={darkInputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth select label="Type" value={cloneForm.target_type}
                onChange={(e) => setCloneForm({ ...cloneForm, target_type: e.target.value })} sx={darkInputSx}>
                {['RTA', 'PTA', 'NTA', 'MTA'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <FormControlLabel
                  control={<Switch checked={cloneForm.clone_settings}
                    onChange={(e) => setCloneForm({ ...cloneForm, clone_settings: e.target.checked })} />}
                  label={<Typography sx={{ color: 'white' }}>Clone Settings</Typography>}
                  labelPlacement="start" sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <FormControlLabel
                  control={<Switch checked={cloneForm.clone_features}
                    onChange={(e) => setCloneForm({ ...cloneForm, clone_features: e.target.checked })} />}
                  label={<Typography sx={{ color: 'white' }}>Clone Features</Typography>}
                  labelPlacement="start" sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCloneOpen(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
          <Button onClick={handleClone} variant="contained" startIcon={<ContentCopy />}
            sx={{ background: 'linear-gradient(135deg,#ec4899,#f43f5e)' }}>
            Clone Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── ADD ADMIN DIALOG ─── */}
      <Dialog open={addAdminOpen} onClose={() => setAddAdminOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e293b', backgroundImage: 'none', borderRadius: 3 } }}>
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd sx={{ color: '#10b981' }} />
          Add Admin for {org?.name || 'Organization'}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="Username *" fullWidth value={addAdminForm.username}
                onChange={(e) => setAddAdminForm({ ...addAdminForm, username: e.target.value })}
                sx={darkInputSx} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Password *" fullWidth type="password" value={addAdminForm.password}
                onChange={(e) => setAddAdminForm({ ...addAdminForm, password: e.target.value })}
                sx={darkInputSx} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email *" fullWidth value={addAdminForm.email}
                onChange={(e) => setAddAdminForm({ ...addAdminForm, email: e.target.value })}
                sx={darkInputSx} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="First Name" fullWidth value={addAdminForm.first_name}
                onChange={(e) => setAddAdminForm({ ...addAdminForm, first_name: e.target.value })}
                sx={darkInputSx} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last Name" fullWidth value={addAdminForm.last_name}
                onChange={(e) => setAddAdminForm({ ...addAdminForm, last_name: e.target.value })}
                sx={darkInputSx} size="small" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddAdminOpen(false)} sx={{ color: '#94a3b8' }} disabled={addAdminSaving}>Cancel</Button>
          <Button onClick={handleAddAdmin} variant="contained" startIcon={addAdminSaving ? <CircularProgress size={18} color="inherit" /> : <PersonAdd />}
            disabled={addAdminSaving || !addAdminForm.username || !addAdminForm.password || !addAdminForm.email}
            sx={{ background: 'linear-gradient(135deg,#10b981,#059669)', '&:hover': { background: 'linear-gradient(135deg,#059669,#047857)' } }}>
            {addAdminSaving ? 'Adding...' : 'Add Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE ORGANIZATION CONFIRM DIALOG ─── */}
      <Dialog open={!!deleteConfirmOrg} onClose={() => setDeleteConfirmOrg(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e293b', backgroundImage: 'none', borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', fontWeight: 'bold' }}>
          Delete Organization
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ color: 'white' }}>
            Are you sure you want to delete <strong>{deleteConfirmOrg?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
            This is a soft delete. The organization will be deactivated and hidden from the
            active list, but no data is removed &mdash; it can be restored from the Trash at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirmOrg(null)} sx={{ color: '#94a3b8' }}
            disabled={orgActionLoadingId === deleteConfirmOrg?.id}>
            Cancel
          </Button>
          <Button onClick={handleDeleteOrg} variant="contained" color="error"
            disabled={orgActionLoadingId === deleteConfirmOrg?.id}
            startIcon={orgActionLoadingId === deleteConfirmOrg?.id ? <CircularProgress size={18} color="inherit" /> : <Delete />}>
            {orgActionLoadingId === deleteConfirmOrg?.id ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationManagement;
